import { routes, type RouteConfig } from "@/app/router/routes";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/libs/utils";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCollectionStatusQuery } from "../hooks/useCollectionStatus";


type LeafRoute = {
  id: string;
  name: string;
  path: string;
  groupId: string;
  groupName: string;
  groupIcon?: RouteConfig["icon"];
};

function flattenLeafRoutes(): LeafRoute[] {
  const leaf: LeafRoute[] = [];

  for (const r of routes) {
    if (r.children?.length) {
      for (const c of r.children) {
        if (!c.path) continue;
        leaf.push({
          id: c.id,
          name: c.name,
          path: c.path,
          groupId: r.id,
          groupName: r.name,
          groupIcon: r.icon,
        });
      }
      continue;
    }

    if (r.path) {
      leaf.push({
        id: r.id,
        name: r.name,
        path: r.path,
        groupId: r.id,
        groupName: r.name,
        groupIcon: r.icon,
      });
    }
  }

  return leaf;
}

export function DashboardPage() {
  const { data: collectionsStatus } = useCollectionStatusQuery();
  const successCount = collectionsStatus?.summary.success_count ?? 0;
  const errorCount = collectionsStatus?.summary.error_count ?? 0;
  const inProgressCount = collectionsStatus?.summary.in_progress_count ?? 0;

  const groups = routes.filter((r) => r.id !== "dashboard");
  const leafRoutes = flattenLeafRoutes().filter((r) => r.path !== "/");

  const [menuKeyword, setMenuKeyword] = useState("");

  const filteredLeafRoutes = useMemo(() => {
    const q = menuKeyword.trim().toLowerCase();
    if (!q) return leafRoutes;

    return leafRoutes.filter((r) => {
      const hay = `${r.groupName} ${r.name} ${r.path}`.toLowerCase();
      return hay.includes(q);
    });
  }, [leafRoutes, menuKeyword]);

  const quickLinks = [
    "/collections/status",
    "/collections/errors",
    "/preferences/collection",
    "/recover/data-forwarder-reprocess",
    "/users",
    "/info/media-api-version",
    "/info/workspace-search",
  ]
    .map((p) => filteredLeafRoutes.find((x) => x.path === p))
    .filter((x): x is LeafRoute => !!x)
    .slice(0, 8);

  return (
    <div className="space-y-6">
      <Breadcrumbs />

      <PageHeader
        title="대시보드"
        actions={
          <Input
            value={menuKeyword}
            onChange={(e) => setMenuKeyword(e.target.value)}
            placeholder="메뉴 검색..."
            className="w-full sm:w-[340px]"
          />
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 왼쪽 절반: 3등분 카드 */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-600">{successCount}</div>
              <p className="text-xs text-muted-foreground">성공한 수집 개수</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-rose-600">{errorCount}</div>
              <p className="text-xs text-muted-foreground">실패한 수집 개수</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{inProgressCount}</div>
              <p className="text-xs text-muted-foreground">진행 중인 수집 개수</p>
            </CardContent>
          </Card>
        </div>

        {/* 오른쪽 절반: 빠른 이동(비율 유지) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">빠른 이동</CardTitle>
            <CardDescription>자주 쓰는 페이지</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {quickLinks.map((r) => (
                <Link key={r.path} to={r.path}>
                  <Button variant="secondary" size="sm" className="gap-2">
                    {r.name}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {groups.map((group) => {
          const Icon = group.icon;
          const children = group.children?.filter((c) => !!c.path) ?? [];

          // 검색 중이면, 그룹의 children도 검색 결과로 필터링
          const filteredChildren = menuKeyword.trim()
            ? children.filter((c) => filteredLeafRoutes.some((r) => r.id === c.id))
            : children;

          const count = filteredChildren.length;

          // 검색 중인데 해당 그룹에 매칭되는 페이지가 없으면 숨김
          if (menuKeyword.trim() && count === 0) return null;


          return (
            <Card key={group.id}>
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
                    <span>{group.name}</span>
                  </CardTitle>
                </div>

                <Badge variant="secondary">{count} pages</Badge>
              </CardHeader>

              <CardContent className="space-y-1">
                {filteredChildren.map((c) => (
                  <Link key={c.id} to={c.path!} className="block">
                    <Button variant="ghost" className={cn("w-full justify-between")}>
                      <span>{c.name}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </Link>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}